import { useState } from "react";
import { FormattedMessage as FM } from "react-intl";

import StdButton from "components/Buttons/StdButton";
import proxy from "api/proxy";
import { TimeIcon, UserIcon } from "components/Icons";

function NotesModal({ record, onSaved, onClose }) {
  const notes = record?.["notes."] ?? [];
  const [readMap, setReadMap] = useState(() => {
    const map = new Map();
    for (const note of notes) {
      map.set(note.id, note.unread !== true);
    }
    return map;
  });
  const [saving, setSaving] = useState(false);

  if (notes.length === 0) {
    return (
      <p className="text-center text-gray-400 py-6">
        <FM id="table.no_data" />
      </p>
    );
  }

  function toggleRead(noteId) {
    setReadMap((prev) => {
      const next = new Map(prev);
      next.set(noteId, !prev.get(noteId));
      return next;
    });
  }

  function markAllRead() {
    setReadMap((prev) => {
      const next = new Map();
      for (const id of prev.keys()) {
        next.set(id, true);
      }
      return next;
    });
  }

  function hasUnreadNow() {
    return notes.some((note) => readMap.get(note.id) === false);
  }

  async function save() {
    const toRead = [];
    const toUnread = [];
    for (const note of notes) {
      const isRead = readMap.get(note.id) === true;
      if (isRead === (note.unread !== true)) continue;
      if (isRead) {
        toRead.push(note.id);
      } else {
        toUnread.push(note.id);
      }
    }
    if (toRead.length === 0 && toUnread.length === 0) {
      onSaved?.(hasUnreadNow());
      onClose?.();
      return;
    }
    setSaving(true);
    let error = null;
    if (toRead.length > 0) {
      const res = await proxy.save({
        model: "ir.note",
        ids: toRead,
        values: { unread: false },
      });
      error = res.error;
    }
    if (!error && toUnread.length > 0) {
      const res = await proxy.save({
        model: "ir.note",
        ids: toUnread,
        values: { unread: true },
      });
      error = res.error;
    }
    setSaving(false);
    if (!error) {
      onSaved?.(hasUnreadNow());
      onClose?.();
    }
  }

  return (
    <div className="py-2">
      <div className="space-y-3">
        {notes.map((note, idx) => {
          const unread = readMap.get(note.id) === false;
          return (
            <div
              key={idx}
              className={`rounded-md border p-3 ${
                unread
                  ? "border-amber-300 bg-amber-50"
                  : "border-gray-200 bg-white opacity-50"
              }`}
            >
              <div className="flex justify-between text-xs text-gray-600 mb-1">
                <div className="flex space-x-2">
                  <span className="flex space-x-2 items-center">
                    <UserIcon className="size-2.5 fill-current text-neutral-800" />
                    <span>{note["create_uid."]?.rec_name}</span>
                  </span>
                  <span className="flex items-center justify-center">|</span>
                  <span className="flex space-x-2 items-center">
                    <span>
                      <TimeIcon className="size-2.5 fill-current text-neutral-800 " />
                    </span>
                    <span>
                      {note.create_date &&
                        new Date(note.create_date)
                          .toLocaleDateString("es-CO", {
                            weekday: "long",
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                          .replace(/de/g, " ")}
                    </span>
                  </span>

                </div>
                <span className="flex items-center gap-2">
                  {unread ? (
                    <span className="inline-block rounded bg-amber-400 px-2 py-0.5 text-xs font-normal text-gray-900">
                      <FM id="notes.unread" />
                    </span>
                  ) : (
                    <span>
                      <FM id="notes.read" />
                    </span>
                  )}
                  <input
                    type="checkbox"
                    checked={!unread}
                    onChange={() => toggleRead(note.id)}
                    className={`size-4 cursor-pointer appearance-none rounded-full border-2 transition-all duration-200 active:scale-90 ${
                      unread
                        ? "border-amber-400 bg-white hover:border-amber-500"
                        : "border-amber-500 bg-cyan-500"
                    }`}
                  />
                </span>
              </div>

              <p className="text-sm text-neutral-800 whitespace-pre-wrap">
                {note.message}
              </p>
            </div>
          );
        })}
      </div>
      <div className="flex justify-center gap-3 pt-4 mt-5">
        {hasUnreadNow() && (
          <StdButton
            color="amber"
            content="button.mark_all_read"
            onClick={markAllRead}
            disabled={saving}
          />
        )}
        <StdButton
          color="blue"
          content="button.save"
          onClick={save}
          disabled={saving}
        />
      </div>
    </div>
  );
}

export default NotesModal;
